import { useEffect, useState } from 'react';
import EventBus from '../../utils/eventBus';
import { supabase } from '../../lib/supabase';
import { useProductSystem } from '../../hooks/useProductSystem';
import { useThemeSettings } from '../../hooks/useThemeSettings';

// Type Json pour les données Supabase
type Json = string | number | boolean | null | { [key: string]: Json } | Json[];

interface Product {
  id: string;
  name: string;
  description?: string | null;
  price?: number | null;
  currency?: string | null;
  image_url?: string | null;
  stock?: number | null;
  discount?: number | null;
  chatbot_public_id?: string;
  created_at?: string;
  updated_at?: string;
  external_id?: string | null;
  category?: string | null;
  metadata?: Record<string, any> | null;
}

// Pour mettre à jour l'interface Window et éviter les erreurs TypeScript
declare global {
  interface Window {
    __openProductModalFallback?: (productId: string) => void;
    openProductModal?: (productId: string) => void;
  }
}

interface ProductModalHandlerProps {
  chatbotPublicId?: string;
}

export function ProductModalHandler({ chatbotPublicId }: ProductModalHandlerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const { settings: productSettings } = useProductSystem();
  const { theme } = useThemeSettings();
  
  // Détecter le mode sombre
  const isDark = theme?.mode === 'dark';
  
  // Paramètres d'affichage des prix et stock
  const showPrices = productSettings?.showPrices !== false; // Par défaut à true
  const showStock = productSettings?.showStock === true;
  
  // Textes en anglais par défaut
  const closeText = "Close";
  const viewProductText = "View product";
  const addToCartText = "Add to cart";
  const stockLabelText = "Stock";

  // Fonction pour récupérer le produit avec gestion explicite du cas où chatbotPublicId est null
  const fetchProductDetails = async (productId: string) => {
    if (!productId) {
      return null;
    }
    
    if (!chatbotPublicId) {
      return null;
    }
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .eq('chatbot_public_id', chatbotPublicId)
        .single();
        
      if (error) {
        return null;
      }
      
      return data as Product;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    // Ne pas initialiser si pas de chatbotPublicId
    if (!chatbotPublicId) {
      return;
    }
    
    // Fonction pour gérer l'ouverture de la modale
    const handleOpenProductModal = async (data: { productId: string }) => {
      if (!data.productId || !chatbotPublicId) {
        return;
      }
      
      // Ouvrir immédiatement la modale avec un état de chargement
      setIsOpen(true);
      setLoading(true);
      
      try {
        // Récupérer les détails du produit depuis la base de données
        const { data: productData, error } = await supabase
          .from('products')
          .select('*')
          .eq('id', data.productId)
          .eq('chatbot_public_id', chatbotPublicId)
          .single();
        
        if (error) {
          setLoading(false);
          return;
        }
        
        if (productData) {
          setProduct(productData as Product);
        }
      } catch (err) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };
    
    // Optimisation: pré-déclarer toutes les méthodes d'ouverture
    const quickOpenModalFallback = (productId: string) => {
      handleOpenProductModal({ productId });
    };
    
    // S'abonner aux événements de l'EventBus d'abord (méthode principale)
    EventBus.on('openProductModal', handleOpenProductModal);
    
    // S'abonner également aux événements DOM (méthode alternative)
    const handleDomEvent = (e: CustomEvent) => {
      if (e.detail && e.detail.productId) {
        handleOpenProductModal({ productId: e.detail.productId });
      }
    };
    
    document.addEventListener('open-product-modal', handleDomEvent as EventListener);
    
    // Ajouter un fallback global pour ouvrir les modales
    if (typeof window !== 'undefined') {
      window.__openProductModalFallback = quickOpenModalFallback;
      
      // IMPORTANT: S'assurer que la fonction window.openProductModal est disponible globalement
      // pour la compatibilité avec les scripts injectés dans le HTML
      window.openProductModal = (productId: string) => {
        if (!productId) return;
        handleOpenProductModal({ productId });
      };
    }
    
    // Cleanup lors du démontage du composant
    return () => {
      EventBus.off('openProductModal', handleOpenProductModal);
      document.removeEventListener('open-product-modal', handleDomEvent as EventListener);
      if (typeof window !== 'undefined') {
        delete window.__openProductModalFallback;
        // Ne pas supprimer window.openProductModal pour éviter
        // de casser les vignettes produits qui pourraient encore être affichées
      }
    };
  }, [chatbotPublicId]);
  
  const closeModal = () => {
    setIsOpen(false);
    // Attendre que l'animation de fermeture soit terminée avant de réinitialiser le produit
    setTimeout(() => {
      setProduct(null);
    }, 300);
  };
  
  const handleActionButtonClick = () => {
    // Vérifier si une URL de redirection est disponible dans les métadonnées
    const redirectUrl = product?.metadata?.redirect_url || 
                        product?.metadata?.['redirect_url'] || 
                        product?.metadata?.url || 
                        product?.metadata?.['product_url'];
    
    if (redirectUrl && typeof redirectUrl === 'string') {
      // Préfixer l'URL avec https:// si nécessaire
      let url = redirectUrl;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    
    // Fermer la modal après le clic
    closeModal();
  };
  
  if (!isOpen || !product) return null;
  
  // Calculer le prix avec réduction si applicable
  const displayPrice = product.price && product.discount 
    ? (product.price * (1 - (product.discount / 100))).toFixed(2) 
    : product.price?.toFixed(2);

  // Vérifier si une URL de redirection est disponible
  const redirectUrl = product?.metadata?.redirect_url || 
                      product?.metadata?.['redirect_url'] || 
                      product?.metadata?.url || 
                      product?.metadata?.['product_url'];
  
  // Déterminer l'étiquette du bouton d'action
  const actionButtonLabel = redirectUrl ? viewProductText : addToCartText;

  return (
    <div 
      className="product-modal-container fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300"
      onClick={closeModal}
    >
      <div 
        className={`product-modal-content rounded-xl overflow-hidden shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col transform transition-transform duration-300 scale-100 ${
          isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-800'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête de la modale */}
        <div className={`p-4 flex justify-between items-center border-b ${
          isDark 
            ? 'bg-gradient-to-r from-[var(--gradient-start)]/20 to-[var(--gradient-end)]/10 border-gray-800' 
            : 'bg-gradient-to-r from-[var(--gradient-start)]/10 to-[var(--gradient-end)]/5 border-[var(--gradient-start)]/15'
        }`}>
          <h3 className="text-lg font-semibold bg-gradient-to-r from-[var(--gradient-start)] to-[var(--gradient-end)] bg-clip-text text-transparent">
            {product.name}
          </h3>
          <button 
            onClick={closeModal}
            className={`p-1 rounded-full transition-colors ${
              isDark 
                ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' 
                : 'hover:bg-gray-200 text-gray-600'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        {/* Corps de la modale */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--gradient-start)]"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {product.image_url && (
                <div className={`relative rounded-lg overflow-hidden shadow-md aspect-video ${
                  isDark ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="object-cover w-full h-full"
                    loading="eager"
                  />
                  {product.discount && (
                    <div className="absolute top-0 left-0 bg-gradient-to-r from-[var(--gradient-start)] to-[var(--gradient-end)] text-white px-3 py-1 text-sm font-semibold shadow-sm">
                      -{product.discount}%
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <h4 className="text-xl font-semibold bg-gradient-to-r from-[var(--gradient-start)] to-[var(--gradient-end)] bg-clip-text text-transparent">
                  {product.name}
                </h4>
                
                {product.description && (
                  <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                    {product.description}
                  </p>
                )}
                
                {product.price && showPrices && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xl font-bold bg-gradient-to-r from-[var(--gradient-start)] to-[var(--gradient-end)] bg-clip-text text-transparent">
                      {displayPrice} {product.currency || 'EUR'}
                    </span>
                    
                    {product.discount && product.discount > 0 && (
                      <span className={`text-sm line-through ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {product.price.toFixed(2)} {product.currency || 'EUR'}
                      </span>
                    )}
                  </div>
                )}
                
                {product.stock !== null && product.stock !== undefined && showStock && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm bg-[var(--gradient-start)]/10 text-[var(--gradient-start)] px-2 py-1 rounded-md font-medium">
                      {stockLabelText}: {product.stock}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className={`p-4 flex justify-end border-t ${
          isDark
            ? 'bg-gradient-to-r from-[var(--gradient-start)]/10 to-[var(--gradient-end)]/10 border-gray-800' 
            : 'bg-gradient-to-r from-[var(--gradient-start)]/5 to-[var(--gradient-end)]/5 border-[var(--gradient-start)]/15'
        }`}>
          <button
            onClick={closeModal}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors mr-2 ${
              isDark 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
          >
            {closeText}
          </button>
          <button
            onClick={handleActionButtonClick}
            className="px-4 py-2 bg-gradient-to-r from-[var(--gradient-start)] to-[var(--gradient-end)] text-white rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
          >
            {actionButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductModalHandler; 